import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';
interface ThemeValue {
  /** What the person chose. `system` is a choice, not the absence of one. */
  preference: ThemePreference;
  /** What is actually rendering right now. */
  theme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}
const Ctx = createContext<ThemeValue | null>(null);
const STORAGE_KEY = 'campuzon.appearance';
function readStored(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // A browser refusing storage is not a reason to fail to render.
  }
  return 'system';
}
function systemTheme(): ResolvedTheme {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Appearance does exactly one thing: set `data-theme` on the document element,
 * which selects a token set. No component branches on the theme, so switching
 * is instant — no reload, no remount, no lost state.
 *
 * The person's own choice is stored, not their operating system's, so an
 * administrator who wants a light console on a dark machine keeps it.
 */
export function ThemeProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStored());
  const [system, setSystem] = useState<ResolvedTheme>(() => systemTheme());

  // Keep following the operating system while the preference is `system`,
  // including a change made after this page loaded.
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!query) return;
    const listen = (event: MediaQueryListEvent) => setSystem(event.matches ? 'dark' : 'light');
    query.addEventListener('change', listen);
    return () => query.removeEventListener('change', listen);
  }, []);
  const theme: ResolvedTheme = preference === 'system' ? system : preference;
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);
  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Honour the choice for this session rather than snapping back.
    }
  }, []);
  const value = useMemo<ThemeValue>(() => ({
    preference,
    theme,
    setPreference
  }), [preference, theme, setPreference]);
  return <Ctx.Provider value={value} data-id="element-5541" data-mp="jlgeut3ljdvt">{children}</Ctx.Provider>;
}
export function useTheme(): ThemeValue {
  const value = useContext(Ctx);
  if (!value) throw new Error('useTheme must be used inside ThemeProvider');
  return value;
}